
import { useCallback, useMemo, useState } from "react";
import { batch, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";
import classNames from "classnames";

import { FeatureFlagsEnum, LanguagesAbbr } from '../../../../types/working-model';
import { useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import Icon, { ICONS, COLORS } from '../../../general-ui/icon';
import { blurEditor, loadWorkingEvent } from '../../../../store/actions/admin/create-event';
import { ParamsProps } from '../../../live-event/live-event';
import EventTranslationsModal from "./event-translations-modal";
import { showAlert } from "@general-ui/alert/alert-service";
import { ALERT_TYPES } from "@general-ui/alert/alert";
import Select from "@general-ui/select/select";
import languageMap from '../../../../utils/translate-langs-map.json';
// The flag icons require country codes, and a language code is not a country code.
// SOME country codes === language code (i.e. Germany)
import countryCodeMap from './language-code-to-country-code-map.json';

import './editor-language-selector.scss';

export function switchingLanguageAlert(): void {
	showAlert({
		message: "Fetching translations",
		description: "Please wait a moment while we get new translations",
		duration: 4000,
		type: ALERT_TYPES.POSITIVE
	});
}

interface ILanguageSelector {
	className?: string;
}

const EditorLanguageSelector = ({ className }: ILanguageSelector) => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);

	const dispatch = useDispatch();
	const history = useHistory();
	const [showEventTranslationsModal, setShowEventTranslationsModal] = useState(false);
	const { id, language: paramLanguage } = useParams() as ParamsProps;

	// if no landing page, then single session w/o landing so pull langs from first session
	// otherwise, pull from homepage langs
	const languages = workingEvent?.homepage ?
		Array.from(new Set(workingEvent?.homepage?.languages as LanguagesAbbr[])) :
		Array.from(new Set(workingEvent?.sessions[0].languages as LanguagesAbbr[]));

	const redirect = useCallback((language: LanguagesAbbr) => {
		// must construct a pathname that includes the current language and it MUST begin with a slash and either end with a slash or be the end of the string
		// this is to prevent a path with a component like '/pages/french-page/fr' navigating to 'en' to change to '/pages/enench-page/fr'

		// the escape chars are not useless, they are needed to escape the forward slashes, eslint is wrong
		// eslint-disable-next-line no-useless-escape
		const regex = new RegExp(`\/(${paramLanguage})($|\/)`);
		history.replace(history.location.pathname.replace(regex, `/${language}/`).replace(/\/$/, ''));
	}, [history, paramLanguage]);

	const handleLanguageClick = async (language: LanguagesAbbr) => {
		if (!token) return;

		switchingLanguageAlert();
		batch(() => {
			dispatch(blurEditor(true));
			dispatch(loadWorkingEvent(id, token));
		});
		
		redirect(language);
	};

	const allLanguages = useMemo(() => {
		if (!workingEvent) {
			return [];
		} else {
			const set = new Set(workingEvent.sessions.flatMap(session => session.languages));
			return Array.from(set).sort();
		}
	}, [workingEvent]);

	if (!featureFlags[FeatureFlagsEnum.ui_ai_captions]) return null;

	return (
		<div className={classNames("editor-language-selector", className)}>
			<Select
				containerClass="editor-language-selection"
				options={languages
						.filter(language => language !== paramLanguage)
						.sort((a, b) => (languageMap[a]) > (languageMap[b]) ? 1 : -1)
						.map(language => ({
						label: languageMap[language],
						value: language,
						// @ts-expect-error Country codes won't perfectly match language codes, safe to ignore TS
						icon: <span className={`fi fi-${countryCodeMap[language]}`}></span>
					}))
				}
				showOptionIcon
				small
				noMaxHeight
				selected={paramLanguage}
				onChange={handleLanguageClick}
				ignorePositioning
				optionContainerTop={70}
				hideEmptyOption
				makeControlledComponent
				mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={14} color={COLORS.WHITE} />}
				iconIfOpen={<Icon name={ICONS.KEYBOARD_ARROW_UP} size={14} color={COLORS.WHITE} />}
				// @ts-expect-error Country codes won't perfectly match language codes, safe to ignore TS
				customSelectedOptionDisplay={<><span className={`fi fi-${countryCodeMap[paramLanguage]}`}></span> {languageMap[paramLanguage]} <span className="editor-language-selector-language-code">{paramLanguage}</span></>}
			/>

			{(featureFlags[FeatureFlagsEnum.translations_v2] && workingEvent) && (
				<EventTranslationsModal
					open={showEventTranslationsModal}
					closeFn={() => setShowEventTranslationsModal(false)}
					languages={allLanguages}
					workingEvent={workingEvent}
				/>
			)}
		</div>
	);
};

export default EditorLanguageSelector;
